/** Your awards: each one arrives face down, then flips over to show what it's for. */

import { AWARD_CAPTIONS } from "../../copy";
import { Fit, Scene, Zone, type BlobSpec } from "../../story/stage";
import { useTimeline } from "../../story/timeline";
import type { Award } from "../../types";
import { AwardBadge } from "../AwardBadge";

const BLOBS: BlobSpec[] = [
  { colour: "fuchsia", x: 60, y: 300, r: 180, seed: 11.3, wob: 0.2 },
  { colour: "blurple", x: 1000, y: 1780, r: 280, seed: 12.2, wob: 0.18 },
];

const ACCENTS = ["var(--wr-yellow)", "var(--wr-fuchsia)"];

const kicker = (n: number) => (n === 1 ? "Your award" : n === 2 ? "Your two awards" : "Your awards");

export function Awards({ awards }: { awards: Award[] }) {
  // The build hands out two each; two cards are what the stage has room for.
  const shown = awards.slice(0, 2);
  const tops = shown.length === 1 ? [740] : [440, 1040];

  const root = useTimeline((s) => {
    s.rise(["[data-part=kick]", "[data-part=lead]"], 0.35, { y: 30, stagger: 0.12 });
    s.q(".flip").forEach((wrap, i) => {
      s.tl.fromTo(wrap, { y: 200, autoAlpha: 0, rotation: i ? 4 : -4 }, { y: 0, autoAlpha: 1, rotation: 0, duration: 0.7, ease: "power3.out" }, 0.5 + i * 0.15);
      const at = 1.3 + i * 1.3;
      s.tl.fromTo(wrap.querySelector(".flip-in"), { rotationY: 0 }, { rotationY: 180, duration: 0.85, ease: "power3.inOut" }, at);
      s.tl.to(wrap, { scale: 1.06, duration: 0.42, yoyo: true, repeat: 1, ease: "sine.inOut" }, at);
      // The turn's whoosh, then a glint as the face comes round.
      s.sound("flip", at, 1, { length: 0.85 });
      s.sound("sparkle", at + 0.5, 1 + i * 0.12);
    });
  });

  return (
    <div ref={root} className="contents">
      <Scene blobs={BLOBS}>
        <Zone at="top">
          <div data-part="kick" className="kick">
            {kicker(shown.length)}
          </div>
          <div data-part="lead" className="lead">
            {shown.length ? "Made up for this chat. Each one goes to one person." : "No awards this time."}
          </div>
        </Zone>
        {shown.map((award, i) => {
          const accent = ACCENTS[i % ACCENTS.length];
          // The first card (or the only one) centres and the second follows the bottom edge,
          // so a taller screen opens the gaps above, between and below them about evenly.
          return (
            <Zone key={award.id} at={i ? "bottom" : "middle"}>
              <div className="flip" style={{ top: tops[i] }}>
                {/* Turned over already: the still frame shows the front. */}
                <div className="flip-in" style={{ transform: "rotateY(180deg)" }}>
                  <div className="fface back" aria-hidden>
                    <div className="q disp">?</div>
                    <div className="mono">
                      AWARD {i + 1} OF {shown.length}
                    </div>
                  </div>
                  <div className="fface front">
                    <div style={{ color: accent }}>
                      <AwardBadge id={award.id} size={96} />
                    </div>
                    <Fit size={128} className="an disp" style={{ color: accent }}>
                      {award.name}
                    </Fit>
                    {award.stat ? (
                      <Fit size={40} min={26} className="as">
                        {award.stat}
                      </Fit>
                    ) : null}
                    <div
                      className="as"
                      style={{
                        marginTop: award.stat ? 12 : 22,
                        font: "500 30px/1.3 var(--wr-font-ui)",
                        color: "var(--wr-soft)",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {AWARD_CAPTIONS[award.id]}
                    </div>
                  </div>
                </div>
              </div>
            </Zone>
          );
        })}
      </Scene>
    </div>
  );
}
